import React, { useState, useMemo } from 'react';
import { PageShell, StatCard, DataTable } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';

const MODALITIES = ['X-Ray', 'Ultrasound (USG)', 'CT Scan', 'MRI', 'Mammography', 'DEXA Scan', 'Fluoroscopy', 'Echo', 'Doppler', 'PET Scan', 'Nuclear Medicine'];
const CONTRAST = ['Without Contrast', 'With Contrast (IV)', 'With Contrast (Oral)', 'With Contrast (IV + Oral)'];
const REPORT_TEMPLATES = {
  'X-Ray': 'CHEST X-RAY REPORT\n\nFindings:\n\nImpression:\n1. ',
  'Ultrasound (USG)': 'ULTRASONOGRAPHY REPORT\n\nFindings:\n\nImpression:\n1. ',
  'CT Scan': 'CT SCAN REPORT\n\nFindings:\n\nImpression:\n1. ',
  MRI: 'MRI REPORT\n\nFindings:\n\nImpression:\n1. ',
};

const STATUS_UI = {
  pending: 'Pending',
  in_progress: 'In Progress',
  reported: 'Reported',
  verified: 'Verified',
};

function uiStatus(s) {
  return STATUS_UI[s] || s || 'Pending';
}

function NewOrderTab({ onDone, user }) {
  const [form, setForm] = useState({
    patient: '', uhid: '', age: '', gender: 'M', modality: 'X-Ray', study: '',
    contrast: 'Without Contrast', clinicalInfo: '', referredBy: user?.name || '', priority: 'Routine', amount: '',
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await ipApi.createRadiologyOrder({
        patientName: form.patient,
        patientId: form.uhid,
        age: form.age,
        gender: form.gender,
        modality: form.modality,
        study: form.study,
        contrast: form.contrast,
        clinicalInfo: form.clinicalInfo,
        referredBy: form.referredBy,
        priority: form.priority,
        amount: Number(form.amount) || 0,
        hospitalId: user?.hospitalId,
      });
      setMsg(`Order placed for ${form.patient}.`);
      setForm({
        patient: '', uhid: '', age: '', gender: 'M', modality: 'X-Ray', study: '',
        contrast: 'Without Contrast', clinicalInfo: '', referredBy: user?.name || '', priority: 'Routine', amount: '',
      });
      onDone?.();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit} style={{ borderLeft: '4px solid #6366f1' }}>
      <h3 style={{ marginTop: 0, color: '#6366f1' }}>New radiology order</h3>
      {msg && <div className="info-banner mb">{msg}</div>}
      {err && <div className="error mb">{err}</div>}
      <div className="form-grid mb">
        <div className="field"><label>Patient name *</label><input className="input" required value={form.patient} onChange={(e) => setForm((f) => ({ ...f, patient: e.target.value }))} /></div>
        <div className="field"><label>UHID</label><input className="input" value={form.uhid} onChange={(e) => setForm((f) => ({ ...f, uhid: e.target.value }))} /></div>
        <div className="field"><label>Age</label><input type="number" className="input" value={form.age} onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))} /></div>
        <div className="field"><label>Gender</label><select className="input" value={form.gender} onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}><option value="M">Male</option><option value="F">Female</option><option value="O">Other</option></select></div>
        <div className="field"><label>Modality *</label><select className="input" required value={form.modality} onChange={(e) => setForm((f) => ({ ...f, modality: e.target.value }))}>{MODALITIES.map((m) => <option key={m}>{m}</option>)}</select></div>
        <div className="field"><label>Study *</label><input className="input" required value={form.study} onChange={(e) => setForm((f) => ({ ...f, study: e.target.value }))} /></div>
        <div className="field"><label>Contrast</label><select className="input" value={form.contrast} onChange={(e) => setForm((f) => ({ ...f, contrast: e.target.value }))}>{CONTRAST.map((c) => <option key={c}>{c}</option>)}</select></div>
        <div className="field"><label>Priority</label><select className="input" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}><option>Routine</option><option>Urgent</option><option>Emergency</option><option>STAT</option></select></div>
        <div className="field"><label>Referred by</label><input className="input" value={form.referredBy} onChange={(e) => setForm((f) => ({ ...f, referredBy: e.target.value }))} /></div>
        <div className="field"><label>Amount (₹)</label><input type="number" className="input" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} /></div>
        <div className="field full"><label>Clinical indication *</label><textarea className="input" rows={3} required value={form.clinicalInfo} onChange={(e) => setForm((f) => ({ ...f, clinicalInfo: e.target.value }))} /></div>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Placing…' : 'Place order'}</button>
      </div>
    </form>
  );
}

function ReportEntryTab({ order, onBack, onSaved }) {
  const { user } = useAuth();
  const [report, setReport] = useState(order.report || REPORT_TEMPLATES[order?.modality] || '');
  const [radiologist, setRadiologist] = useState(order.reportedBy || user?.name || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function saveReport(e) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await ipApi.patchRadiologyOrder(order.orderId, {
        report,
        reportedBy: radiologist,
        status: 'reported',
      });
      await onSaved?.();
      onBack?.();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ borderLeft: '4px solid #10b981' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: '#10b981' }}>Report — {order?.study}</h3>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
      </div>
      {err && <div className="error mb">{err}</div>}
      <form onSubmit={saveReport}>
        <div className="field mb"><label>Radiologist</label><input className="input" required value={radiologist} onChange={(e) => setRadiologist(e.target.value)} /></div>
        <div className="field mb"><label>Report</label><textarea className="input" rows={18} value={report} onChange={(e) => setReport(e.target.value)} style={{ fontFamily: 'monospace', fontSize: 13 }} /></div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => window.print()}>Print</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save & verify'}</button>
        </div>
      </form>
    </div>
  );
}

function TatAnalyticsTab({ orders }) {
  const reported = orders.filter((o) => ['reported', 'verified'].includes(o.status));
  const byModality = MODALITIES.slice(0, 6).map((m) => {
    const rows = orders.filter((o) => o.modality === m);
    const done = rows.filter((o) => ['reported', 'verified'].includes(o.status));
    const tatMins = done.map((o) => {
      if (!o.createdAt || !o.reportedAt) return null;
      return Math.round((new Date(o.reportedAt) - new Date(o.createdAt)) / 60000);
    }).filter((x) => x != null && x >= 0);
    const avg = tatMins.length ? Math.round(tatMins.reduce((a, b) => a + b, 0) / tatMins.length) : null;
    return { modality: m, count: rows.length, reported: done.length, avgTat: avg };
  });

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Modality performance (live)</h3>
      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th>Modality</th><th>Orders</th><th>Reported</th><th>Avg TAT (min)</th></tr></thead>
          <tbody>
            {byModality.map((m) => (
              <tr key={m.modality}>
                <td>{m.modality}</td>
                <td>{m.count}</td>
                <td>{m.reported}</td>
                <td>{m.avgTat != null ? m.avgTat : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function RadiologyPage() {
  const { user } = useAuth();
  const { data, loading, error, reload } = useLiveData(() => ipApi.getRadiologyOrders(80), []);
  const orders = useMemo(() => (data?.orders || []).map((o) => ({ ...o, id: o.orderId, statusUi: uiStatus(o.status) })), [data]);
  const [tab, setTab] = useState('worklist');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');

  const filtered = statusFilter === 'All' ? orders : orders.filter((o) => uiStatus(o.status) === statusFilter);
  const pColor = { Urgent: '#f97316', Emergency: '#dc2626', Routine: '#10b981', STAT: '#dc2626' };
  const sColor = { Pending: '#f59e0b', 'In Progress': '#3b82f6', Reported: '#10b981', Verified: '#6366f1' };

  const columns = [
    { key: 'orderId', label: 'Order ID', render: (r) => <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6366f1', fontWeight: 700 }}>{r.orderId}</span> },
    { key: 'patient', label: 'Patient', render: (r) => <div><div style={{ fontWeight: 700 }}>{r.patientName}</div><div style={{ fontSize: 11, color: '#94a3b8' }}>{r.patientId} · {r.age}y/{r.gender}</div></div> },
    { key: 'modality', label: 'Modality', render: (r) => <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#f1f5f9' }}>{r.modality}</span> },
    { key: 'study', label: 'Study', render: (r) => <div><div style={{ fontWeight: 600, fontSize: 13 }}>{r.study}</div><div style={{ fontSize: 11, color: '#64748b' }}>{r.referredBy}</div></div> },
    { key: 'priority', label: 'Priority', render: (r) => <span style={{ fontSize: 11, fontWeight: 800, color: pColor[r.priority] || '#94a3b8' }}>{r.priority}</span> },
    { key: 'status', label: 'Status', render: (r) => <span style={{ fontSize: 11, fontWeight: 700, color: sColor[r.statusUi] || '#64748b' }}>{r.statusUi}</span> },
    { key: 'actions', label: 'Actions', render: (r) => (
      <div style={{ display: 'flex', gap: 4 }}>
        {r.status === 'pending' && <button type="button" className="btn btn-sm btn-secondary" onClick={async () => { await ipApi.patchRadiologyOrder(r.orderId, { status: 'in_progress' }); await reload(); }}>Start</button>}
        {r.status !== 'reported' && r.status !== 'verified' && (
          <button type="button" className="btn btn-sm btn-primary" onClick={() => { setSelectedOrder(r); setTab('report'); }}>Report</button>
        )}
      </div>
    )},
  ];

  return (
    <PageShell
      title="Radiology / RIS"
      subtitle="Live worklist · orders · reporting · TAT"
      actions={<button type="button" className="btn btn-secondary btn-sm" onClick={reload}>Refresh</button>}
    >
      <div className="stat-grid">
        <StatCard label="Total" value={orders.length} accent="#6366f1" loading={loading} />
        <StatCard label="Pending" value={orders.filter((o) => o.status === 'pending').length} accent="#f59e0b" loading={loading} />
        <StatCard label="In progress" value={orders.filter((o) => o.status === 'in_progress').length} accent="#3b82f6" loading={loading} />
        <StatCard label="Reported" value={orders.filter((o) => ['reported', 'verified'].includes(o.status)).length} accent="#10b981" loading={loading} />
      </div>

      {error && <div className="error mb">{error}</div>}

      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'worklist', label: 'Worklist' }, { id: 'new', label: '+ New order' }, { id: 'analytics', label: 'TAT analytics' }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => { setTab(id); setSelectedOrder(null); }}>{label}</button>
        ))}
      </div>

      {tab === 'report' && selectedOrder && (
        <ReportEntryTab order={selectedOrder} onBack={() => setTab('worklist')} onSaved={reload} />
      )}

      {tab === 'worklist' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {['All', 'Pending', 'In Progress', 'Reported'].map((s) => (
              <button key={s} type="button" className={`emr-chip ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
                {s} {s === 'All' ? `(${orders.length})` : `(${orders.filter((o) => uiStatus(o.status) === s).length})`}
              </button>
            ))}
          </div>
          <DataTable columns={columns} rows={filtered} keyField="orderId" loading={loading} emptyMessage="No radiology orders yet." />
        </>
      )}
      {tab === 'new' && <NewOrderTab user={user} onDone={() => { reload(); setTab('worklist'); }} />}
      {tab === 'analytics' && <TatAnalyticsTab orders={orders} />}
    </PageShell>
  );
}
